import React, { FC, useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemText,
  useTheme,
  CircularProgress
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { Badge } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useCurrentUser } from "hooks";
import { updateMileageDeviationStatusOnlyThunk } from "app/features/mileageDeviations/mileageDeviationsAction";

interface StatusDropdownCellProps {
  currentStatus: string;
  rowData: any;
}

const StatusDropdownCell: FC<StatusDropdownCellProps> = ({
  currentStatus,
  rowData
}) => {
  const { t } = useTranslation(["translation"]);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { accountId, permissions, userEmail } = useCurrentUser();
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const hasPermission = permissions.canEditMileageDeviationStatus;

  const STATUS_OPTIONS = [
    { value: "New", label: t("status.new"), color: "#1976D2" },
    { value: "Resolved", label: t("status.resolved"), color: "#2E7D32" },
    { value: "Denied", label: t("status.denied"), color: "#D32F2F" }
  ];

  useEffect(() => {
    if (isLocalLoading && anchorEl) {
      setAnchorEl(null);
    }
  }, [isLocalLoading, anchorEl]);

  const getStatusColorScheme = (status: string) => {
    switch (status?.toLowerCase()) {
      case "new":
        return "info-blue";
      case "resolved":
        return "success";
      case "denied":
        return "error";
      default:
        return "grey";
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasPermission && !isLocalLoading) {
      setAnchorEl(buttonRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = async (newStatus: string) => {
    handleClose();
    setIsLocalLoading(true);

    const statusMap: { [key: string]: number } = {
      New: 0,
      Resolved: 1,
      Denied: 2
    };

    try {
      await dispatch(
        updateMileageDeviationStatusOnlyThunk({
          accountId: accountId || "",
          objectId: rowData.objectUniqueId || rowData.uniqueId,
          deviationId: rowData.uniqueId,
          status: statusMap[newStatus],
          userEmail: userEmail || undefined
        })
      ).unwrap();
    } catch (error) {
      // Error handling is done in the thunk
    } finally {
      setIsLocalLoading(false);
    }
  };

  const open = Boolean(anchorEl);
  const availableOptions = STATUS_OPTIONS.filter(
    (option) => option.value.toLowerCase() !== currentStatus?.toLowerCase()
  );

  const getTranslatedStatus = (status: string) => {
    switch (status?.toLowerCase()) {
      case "new":
        return t("status.new");
      case "resolved":
        return t("status.resolved");
      case "denied":
        return t("status.denied");
      default:
        return status;
    }
  };

  return (
    <>
      <Box
        ref={buttonRef}
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: hasPermission && !isLocalLoading ? "pointer" : "default",
          opacity: hasPermission && !isLocalLoading ? 1 : 0.6,
          "&:hover": {
            opacity: hasPermission && !isLocalLoading ? 0.8 : 0.6
          }
        }}
      >
        <Badge colorScheme={getStatusColorScheme(currentStatus)}>
          <Box sx={{ ml: 1, display: "flex", alignItems: "center", gap: 1 }}>
            {getTranslatedStatus(currentStatus)}
            {hasPermission &&
              (isLocalLoading ? (
                <CircularProgress
                  size={16}
                  sx={{
                    color: "#666"
                  }}
                />
              ) : (
                <ArrowDropDown
                  sx={{
                    fontSize: 18,
                    color: getStatusColorScheme(currentStatus),
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s"
                  }}
                />
              ))}
          </Box>
        </Badge>
      </Box>

      <Popover
        open={open && !isLocalLoading}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            border: "1px solid #E0E0E0",
            minWidth: 150
          }
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "#959EB2",
              textTransform: "uppercase",
              width: "100%",
              fontSize: "11px",
              backgroundColor: "#F9FBFF",
              paddingTop: 0.5,
              paddingBottom: 0.5,
              paddingLeft: 2,
              paddingRight: 2,
              borderBottom: "1px solid #E0E0E0"
            }}
          >
            {t("common.updateTo")}
          </Typography>
          <List dense sx={{ py: 0 }}>
            {availableOptions.map((option) => (
              <ListItem
                key={option.value}
                onClick={() => handleStatusSelect(option.value)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  paddingTop: 0.5,
                  paddingBottom: 0.5,
                  paddingLeft: 2,
                  paddingRight: 2,
                  borderBottom: "1px solid #E0E0E0",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <ListItemText
                  primary={option.label}
                  primaryTypographyProps={{
                    fontSize: "14px",
                    fontWeight: 500
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
};

export default StatusDropdownCell;
